import { Link } from "react-router";
import { roleHome } from "@/modules/identity/surfaces";
import { DataUnavailable } from "@/design/DataUnavailable";
import { PageShell } from "@/design/ui";
import { useMe } from "~/auth/MeProvider";
import { ConsumerChrome } from "~/components/ConsumerChrome";
import { ScreenPending } from "~/components/ScreenPending";
import { useQuery } from "~/lib/useQuery";

/**
 * InboxScreen (docs/ux/screens/InboxScreen.md). Opening it marks everything
 * read.
 *
 * **`?markRead=1` is why that is a query parameter rather than the default.**
 * The header's bell reads the same endpoint on every signed-in screen to show
 * its count, and an endpoint that marked on every read would zero its own badge
 * on every page view: the reader would never learn which items were new. So
 * marking is an explicit act, and opening this screen is the act.
 *
 * **Not consumer-only.** A member or an admin has an inbox too (the endpoint
 * takes any account), which is why the "Your bookings" link and the way back
 * are role-dependent rather than assumed.
 */
interface InboxPayload {
  entries: {
    id: string;
    title: string;
    body: string;
    createdAt: string;
    unread: boolean;
  }[];
}

export function InboxScreen() {
  const { me } = useMe();
  const inbox = useQuery<InboxPayload>("/inbox?markRead=1");
  const isConsumer = me?.kind === "consumer";

  return (
    <main className="flex min-h-screen grow flex-col bg-page text-body">
      {isConsumer && <ConsumerChrome />}
      <PageShell>
        <div className="mx-auto w-full max-w-md">
          <div className="mb-6 flex items-center justify-between">
            <h1 className="text-xl font-bold text-ink">Inbox</h1>
            <div className="flex items-center gap-4">
              {isConsumer && (
                <Link
                  to="/bookings"
                  className="text-sm font-medium text-accent"
                >
                  Your bookings
                </Link>
              )}
              <Link
                to={me && me.kind !== "guest" ? roleHome(me.kind) : "/"}
                className="text-sm font-medium text-accent"
              >
                Back
              </Link>
            </div>
          </div>

          {inbox.loading && !inbox.data ? (
            <ScreenPending />
          ) : inbox.failure ? (
            <DataUnavailable
              configured={inbox.failure !== "not_configured"}
              what="your inbox"
            />
          ) : inbox.data && inbox.data.entries.length === 0 ? (
            <p className="rounded-xl border border-line bg-surface p-8 text-center text-sm text-muted">
              Nothing here yet. We&apos;ll notify you when something needs your
              attention.
            </p>
          ) : inbox.data ? (
            <ul className="space-y-3">
              {inbox.data.entries.map((entry) => (
                <li
                  key={entry.id}
                  className={`rounded-xl border border-line bg-surface p-4 ${
                    entry.unread ? "border-s-4 border-s-accent" : ""
                  }`}
                >
                  <p
                    className={`text-sm ${
                      entry.unread ? "font-bold" : "font-medium"
                    } text-ink`}
                  >
                    {entry.title}
                  </p>
                  <p className="mt-1 text-sm text-body">{entry.body}</p>
                  <p className="mt-2 font-mono text-xs text-muted">
                    {new Date(entry.createdAt).toLocaleString()}
                  </p>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </PageShell>
    </main>
  );
}
